import {WarehouseRelationshipData} from 'insights/warehouse/warehouseModelsDataLayout';
import {Point} from 'util/geometry/GeometryConstants';
import ImmutableMap from 'util/ImmutableMap';
import ImmutableSet from 'util/ImmutableSet';

/**
 * 2-dimensional grid cell, with up to one table in any given coordinate.
 */
export type DiagramGridCell = Point;

/**
 * Pixel translation relative to the top-left of grid cell (0, 0).
 */
export type DiagramPixelPoint = Point;

/**
 * The data required for drawing a relationship path.
 *
 * sourceExitSide is the side from which the relationship path leaves the source table (i.e. the right side).
 * targetEntranceSide is the side into which the relationship path enters the target table (i.e. the left side).
 */
export interface WarehouseRelationshipPath {
  points: Array<DiagramPixelPoint>;
  sourceExitSide: VerticalSide;
  targetEntranceSide: VerticalSide;
}

export interface WarehouseTooltipData {
  relationshipData: WarehouseRelationshipData;
  triggerPoint: Point;
}

/**
 * The DiagramLayout contains all table location & state information for the dynamic warehouse diagram.
 *
 * The tables are arranged in an unbounded two dimensional grid.
 *
 * The data of where the tables are in the diagram is stored in a "bidirectional map" of
 * grid cell <-> tableName (tableNameToCell and cellToTableName),
 * allowing efficient lookup given either piece of information.
 *
 * The x-axis is positive in the rightward direction. The y-axis is positive in the downard direction.
 * Therefore, (1, -1) is rendered to the right and above (0, 0).
 *
 * See this image for a graphical representation of the grid layout:
 *   https://user-images.githubusercontent.com/10499901/87957698-999b4000-ca7e-11ea-950b-64d4a6aaf576.png
 *
 * @param tableNameToCell: ImmutableMap of tableName string -> (grid cell that tableName is at in the diagram)
 * @param cellToTableName: ImmutableMap of grid cell -> (tableName located at that point)
 * @param verticallyExpandedTables: ImmutableSet of tables which have all of their columns being displayed
 */
export interface DiagramLayout {
  readonly tableNameToCell: ImmutableMap<string, DiagramGridCell>;
  readonly cellToTableName: ImmutableMap<DiagramGridCell, string>;
  readonly verticallyExpandedTables: ImmutableSet<string>;
  readonly hoveredRelationship: WarehouseRelationshipData | null;
  readonly relationshipTooltip: WarehouseTooltipData | null;
}

export enum VerticalSide {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum HorizontalSide {
  TOP = 'top',
  BOTTOM = 'bottom',
}
